// src/hooks/useAccountContext.ts
import { useState, useCallback, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { ME, GET_USER_BY_ID } from '../graphql/queries';
import { UPDATE_USER_PROFILE, CHANGE_PASSWORD } from '../graphql/mutations';

const MAX_FILE_SIZE_MB = 7;
const MAX_DIMENSIONS = 1024;

export const useAccountContext = ({ id }: { id?: string } = {}) => {
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword1, setNewPassword1] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [adminComment, setAdminComment] = useState('');

  // ✅ Состояние для полей
  const [username, setUsername] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  const { data, loading } = useQuery(id ? GET_USER_BY_ID : ME, {
    variables: id ? { id } : {},
    // 🔥 ИСПРАВЛЕНИЕ: network-only гарантирует, что при входе в свой профиль 
    // мы ВСЕГДА получаем свежие данные, а не закешированные данные чужого пользователя.
    fetchPolicy: id ? 'cache-and-network' : 'network-only', 
    onCompleted: (fetchedData) => {
      const user = id ? fetchedData.userById : fetchedData.me;
      if (user) {
        setUsername(user.username);
        setPhone(user.phone);
        setEmail(user.email);
        setCompany(user.company || '');
        setImageSrc(user.avatar || null);
        setAdminComment(user.adminComment || ''); // ✅ Инициализируем заметку
      }
    },
    onError: (error) => {
      console.error('Ошибка загрузки пользователя:', error);
      setMessage({ text: `Ошибка: ${error.message}`, type: 'error' });
      setTimeout(() => setMessage(null), 7000);
    }
  });

  const [updateProfile] = useMutation(UPDATE_USER_PROFILE);
  const [changePassword] = useMutation(CHANGE_PASSWORD);

  const handleAvatarChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setFileError(`Файл слишком большой. Максимальный размер: ${MAX_FILE_SIZE_MB}MB`);
      return;
    }
    if (!file.type.match(/image\/(jpeg|png|gif|webp)/)) {
      setFileError('Допустимые форматы: JPEG, PNG, GIF, WEBP');
      return;
    }

    setFileError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        if (img.width > MAX_DIMENSIONS || img.height > MAX_DIMENSIONS) {
          setFileError(`Изображение слишком большое. Максимальный размер: ${MAX_DIMENSIONS}x${MAX_DIMENSIONS}px`);
          return;
        }
        setImageSrc(img.src);
        setIsCropModalOpen(true);
      };
      img.onerror = () => setFileError('Ошибка загрузки изображения');
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }, []);

  const handleSaveAvatar = useCallback(
    async (croppedImageBase64: string) => {
      if (!croppedImageBase64) return;

      try {
        await updateProfile({
          variables: {
            ...(id ? { id } : {}),
            username,
            email,
            phone,
            company,
            avatar: croppedImageBase64,
          },
        });

        setImageSrc(croppedImageBase64);

        setMessage({
          text: 'Аватар успешно обновлён',
          type: 'success',
        });

        setTimeout(() => setMessage(null), 7000);

        setIsCropModalOpen(false);
      } catch (error: any) {
        setMessage({
          text:
            error.message ||
            'Ошибка при сохранении аватара',
          type: 'error',
        });

        setTimeout(() => setMessage(null), 7000);
      }
    },
    [
      updateProfile,
      id,
      username,
      email,
      phone,
      company,
    ]
  );

  // 🔹 Нормализация телефона: возвращает +79991234567
  const normalizePhone = (value: string): string | null => {
    const raw = value.replace(/\D/g, '');
    if (raw.length === 11) {
      if (raw.startsWith('8')) return '+7' + raw.slice(1);
      if (raw.startsWith('7')) return '+' + raw;
    } else if (raw.length === 10) {
      return '+7' + raw;
    }
    return null; // ошибка формата
  };

  const handleSaveProfile = useCallback(async () => {
    // Проверка обязательных полей
    if (!username || !username.trim()) {
      setMessage({ text: 'Имя пользователя обязательно', type: 'error' });
      setTimeout(() => setMessage(null), 7000);
      return;
    }
    if (!email || !email.trim()) {
      setMessage({ text: 'Email обязателен', type: 'error' });
      setTimeout(() => setMessage(null), 7000);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMessage({ text: 'Некорректный email', type: 'error' });
      setTimeout(() => setMessage(null), 7000);
      return;
    }
    if (!phone || !phone.trim()) {
      setMessage({ text: 'Телефон обязателен', type: 'error' });
      setTimeout(() => setMessage(null), 7000);
      return;
    }
    const normalizedPhone = normalizePhone(phone);
    if (!normalizedPhone) {
      setMessage({ text: 'Введите корректный номер телефона', type: 'error' });
      setTimeout(() => setMessage(null), 7000);
      return;
    }
    try {
      await updateProfile({
        variables: {
          ...(id ? { id } : {}),
          username,
          email,
          phone: normalizedPhone, 
          company: company || undefined,
          avatar: imageSrc || undefined,
           adminComment: adminComment || undefined,
        },
      });
      setMessage({ text: 'Профиль успешно обновлён', type: 'success' });
      setTimeout(() => setMessage(null), 7000);
    } catch (error: any) {
      setMessage({ text: error.message || 'Ошибка при сохранении', type: 'error' });
      setTimeout(() => setMessage(null), 7000);
    }
  }, [updateProfile, id, username, email, phone, company, imageSrc, adminComment]);

  const handleChangePassword = useCallback(async () => {
    if (newPassword1 !== newPassword2) {
      setMessage({ text: 'Новые пароли не совпадают', type: 'error' });
      setTimeout(() => setMessage(null), 7000);
      return;
    }
    if (newPassword1 === oldPassword) {
      setMessage({ text: 'Старый и новый пароль совпадают', type: 'error' });
      setTimeout(() => setMessage(null), 7000);
      return;
    }
    try {
      await changePassword({
        variables: { oldPassword, newPassword: newPassword1 },
      });
      setMessage({ text: 'Пароль успешно изменён', type: 'success' });
      setTimeout(() => setMessage(null), 7000);
      setIsModalOpen(false);
    } catch (error: any) {
      setMessage({ text: error.message || 'Ошибка при изменении пароля', type: 'error' });
      setTimeout(() => setMessage(null), 7000);
    }
  }, [oldPassword, newPassword1, newPassword2, changePassword]);

  const handleSignOut = useCallback(() => {
    localStorage.removeItem('token');
    window.location.href = '/Home';
  }, []);

  const handleEditAvatarClick = useCallback(() => {
    setFileError(null);
    fileInputRef.current?.click();
  }, []);

  return {
    // State
    data,
    loading,
    message,
    fileError,
    isModalOpen,
    oldPassword,
    newPassword1,
    newPassword2,
    imageSrc,
    username,
    email,
    phone,
    company,
    isCropModalOpen,
    adminComment,
    // Refs
    fileInputRef,
    // Setters
    setUsername,
    setEmail,
    setPhone,
    setCompany,
    setOldPassword,
    setNewPassword1,
    setNewPassword2,
    setImageSrc,
    setIsModalOpen,
    setIsCropModalOpen,
    setFileError,
    setMessage,
    setAdminComment,
    // Actions
    handleAvatarChange,
    handleSaveAvatar,
    handleSaveProfile,
    handleChangePassword,
    handleSignOut,
    handleEditAvatarClick,
  };
};